import {Lock, RefreshCw, TriangleAlert} from 'lucide-react';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  Checkbox,
  Input,
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  Textarea
} from '@kroll-resolver-crisp/ui-components-react';
import type {GroundingPreview} from '../../api/workflows';

const STEPS = ['File', 'Check', 'Submit'] as const;

export function ReplaceCsvSheet({
  open,
  busy,
  blocked,
  workflowName,
  workflowKey,
  csv,
  onCsvChange,
  preview,
  confirm,
  onConfirmChange,
  onCheck,
  onSubmit,
  onStartFromCurrent,
  onClose
}: {
  readonly open: boolean;
  readonly busy: boolean;
  readonly blocked: boolean;
  readonly workflowName: string;
  readonly workflowKey: string;
  readonly csv: string;
  readonly onCsvChange: (value: string) => void;
  readonly preview: GroundingPreview | null;
  readonly confirm: boolean;
  readonly onConfirmChange: (value: boolean) => void;
  readonly onCheck: () => void;
  readonly onSubmit: () => void;
  readonly onStartFromCurrent: () => void;
  readonly onClose: () => void;
}) {
  const hasErrors = (preview?.errors.length ?? 0) > 0;
  const destructive = Boolean(
    preview && !hasErrors && (preview.changed_count > 0 || preview.retired_count > 0)
  );
  const step = csv.trim() ? (!preview || hasErrors ? 1 : 2) : 0;
  const checked = preview !== null && !hasErrors;

  return (
    <Sheet
      open={open}
      onOpenChange={(next) => {
        if (!next) {
          onClose();
        }
      }}>
      <SheetContent className="w-[min(720px,100vw)] max-w-[100vw] sm:max-w-[720px]">
        <SheetHeader>
          <SheetTitle>Replace grounding</SheetTitle>
          <SheetDescription>
            {workflowName} · <code className="font-mono text-xs">{workflowKey}</code>
          </SheetDescription>
        </SheetHeader>
        <div className="flex flex-1 flex-col gap-4 overflow-y-auto px-4 pb-4">
          <ol className="grid grid-cols-3 gap-2 text-xs">
            {STEPS.map((name, index) => (
              <li
                key={name}
                className={`border-b-2 pb-1 ${index === step ? 'border-primary text-foreground font-medium' : index < step ? 'border-primary/50 text-muted-foreground' : 'border-border text-muted-foreground'}`}>
                {index + 1}. {name}
              </li>
            ))}
          </ol>
          {blocked ? (
            <Alert variant="destructive">
              <Lock aria-hidden />
              <AlertTitle>A proposal is already pending</AlertTitle>
              <AlertDescription>Approve or reject it before submitting another.</AlertDescription>
            </Alert>
          ) : null}
          <Alert>
            <RefreshCw aria-hidden />
            <AlertTitle>The file replaces everything</AlertTitle>
            <AlertDescription>
              Rows with an entry_id keep or update that entry. Rows without one are added. Published
              entries missing from the file are retired once approved.
            </AlertDescription>
          </Alert>
          <section className="flex flex-col gap-2">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold">CSV file</h3>
              <Button size="sm" variant="ghost" onClick={onStartFromCurrent}>
                Start from current
              </Button>
            </div>
            <Input
              type="file"
              accept=".csv,text/csv"
              aria-label="Choose a CSV file"
              onChange={(event) => {
                const file = event.target.files?.[0];
                if (file) {
                  void file.text().then(onCsvChange);
                }
              }}
            />
            <Textarea
              aria-label="Grounding CSV"
              className="font-mono text-xs"
              rows={8}
              value={csv}
              onChange={(event) => {
                onCsvChange(event.target.value);
              }}
            />
          </section>
          {preview ? (
            <section className="flex flex-col gap-2.5">
              <h3 className="text-sm font-semibold">Check</h3>
              {hasErrors ? (
                <Alert variant="destructive">
                  <TriangleAlert aria-hidden />
                  <AlertTitle>
                    {preview.errors.length} problem{preview.errors.length > 1 ? 's' : ''} to fix
                  </AlertTitle>
                  <AlertDescription>
                    <ul className="m-0 list-disc pl-4">
                      {preview.errors.map((item) => (
                        <li key={item}>{item}</li>
                      ))}
                    </ul>
                  </AlertDescription>
                </Alert>
              ) : (
                <>
                  <div className="grid grid-cols-3 gap-2">
                    {(
                      [
                        ['Added', preview.added_count],
                        ['Changed', preview.changed_count],
                        ['Retired', preview.retired_count]
                      ] as const
                    ).map(([name, value]) => (
                      <div key={name} className="border-border bg-card rounded-md border p-3">
                        <div className="text-xl font-semibold tabular-nums">{value}</div>
                        <div className="text-muted-foreground text-xs">{name}</div>
                      </div>
                    ))}
                  </div>
                  {destructive ? (
                    <label className="flex items-start gap-2 text-sm">
                      <Checkbox
                        checked={confirm}
                        onCheckedChange={(value) => {
                          onConfirmChange(value);
                        }}
                      />
                      <span>
                        I reviewed and confirm this replacement, including retired entries.
                      </span>
                    </label>
                  ) : null}
                </>
              )}
            </section>
          ) : null}
        </div>
        <div className="border-border flex justify-end gap-2 border-t p-4">
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          {checked ? (
            <Button disabled={busy || blocked || (destructive && !confirm)} onClick={onSubmit}>
              Submit for approval
            </Button>
          ) : (
            <Button disabled={busy || blocked || !csv.trim()} onClick={onCheck}>
              Check file
            </Button>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
